import React from "react";
import CardBase from "@/components/cards/CardBase";
import IconProvider from "@/components/ui/IconProvider";
import { DataSourceType } from "@/lib/dataSources";
import { useDataSource } from "@/lib/useDataSource";

interface StatsWidgetProps {
  value?: string;
  label?: string;
  trend?: string;
  icon?: React.ReactNode;
  iconBg?: string; // Tailwind color class or CSS color
  showTitle?: boolean;
  dataSource?: DataSourceType;
  backgroundColor?: string;
  shadowType?: 'black' | 'gradient' | 'none' | 'color' | 'theme';
  shadowColor?: string;
}

const StatsWidget: React.FC<StatsWidgetProps> = ({
  value,
  label,
  trend,
  icon,
  iconBg,
  showTitle = true,
  dataSource,
  backgroundColor, // Remove hardcoded default
  shadowType = 'theme', // Use theme-aware shadow
  shadowColor = '#000000'
}) => {
  // Use data source if provided
  const { data: sourceData, isLoading } = useDataSource(dataSource || 'library');
  
  // Determine the display value
  // Only use data source if explicitly provided
  const displayValue = dataSource ?
    (isLoading ? '...' : (sourceData?.length ?? 0).toString()) :
    value || '0';

  return (
    <CardBase 
      className="flex flex-col h-full w-full flex-1 pb-4" 
      shadowType={shadowType} 
      shadowColor={shadowColor} 
      backgroundColor={backgroundColor}
    >
      {showTitle && (
        <div className="p-4 pb-2 border-b border-border">
          <span className="text-lg font-semibold text-foreground">
            {label}
          </span>
        </div>
      )}
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground"></div>
        </div>
      )}
      <div className={`${showTitle ? 'px-4' : 'p-4'} flex flex-col gap-2 flex-1 w-full`}>
        {displayValue && (
          <span className="text-2xl font-bold text-foreground leading-none pt-4">
            {displayValue}
          </span>
        )}

        {trend && (
          <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium mt-1">
            {trend}
          </div>
        )}
      </div>
    </CardBase>
  );
};

export default StatsWidget;